import { useEffect, useMemo, useState } from "react";
import { FreeNumberInput } from "@/components/ui/free-number-input";
import {
  Brain, FloppyDisk, ArrowCounterClockwise, CaretDown, CaretRight,
  ArrowsOutSimple, ShieldStar, ChartLineUp, Info,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * PlatformRagPanel — Feb 2026 (Randy pt-rag-lift).
 *
 * Super-admin control surface for MMR (Maximal Marginal Relevance) —
 * the retrieval-diversity re-ranker that sits on top of every RAG
 * consumer on the platform (Public QA, YT Replies, AI Assistant,
 * Battle, Probe). Docs live in-panel because MMR is one of those knobs
 * you need to understand before you touch, and burying the explainer
 * in a tooltip would guarantee bad edits.
 *
 * Backed by the existing `platform_thresholds` singleton doc + PATCH
 * endpoint (`/api/admin/thresholds`). Server hot-reloads the config
 * into `rag.chroma_manager` on every save — no pod restart required.
 */
export default function PlatformRagPanel() {
  const [server, setServer] = useState(null); // {thresholds, defaults}
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/thresholds");
      setServer(r.data);
      setDraft({
        rag_mmr_enabled: r.data.thresholds.rag_mmr_enabled,
        rag_mmr_lambda: r.data.thresholds.rag_mmr_lambda,
        rag_mmr_warm_start: r.data.thresholds.rag_mmr_warm_start,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load retrieval config.");
    }
  };

  useEffect(() => { load(); }, []);

  const dirtyKeys = useMemo(() => {
    if (!server) return [];
    return Object.keys(draft).filter((k) => draft[k] !== server.thresholds[k]);
  }, [draft, server]);

  const save = async () => {
    if (!dirtyKeys.length) return;
    setBusy(true);
    try {
      const updates = Object.fromEntries(dirtyKeys.map((k) => [k, draft[k]]));
      const r = await api.patch("/admin/thresholds", { updates });
      setServer({ ...server, thresholds: r.data.thresholds });
      toast.success(`Retrieval config updated. Hot-reloaded across the platform.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };

  const resetToDefaults = async () => {
    if (!server) return;
    setBusy(true);
    try {
      const updates = {
        rag_mmr_enabled: server.defaults.rag_mmr_enabled,
        rag_mmr_lambda: server.defaults.rag_mmr_lambda,
        rag_mmr_warm_start: server.defaults.rag_mmr_warm_start,
      };
      const r = await api.patch("/admin/thresholds", { updates });
      setServer({ ...server, thresholds: r.data.thresholds });
      setDraft(updates);
      toast.success("Reset to platform defaults.");
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed."); }
    finally { setBusy(false); }
  };

  if (!server) return null;

  const enabled = !!draft.rag_mmr_enabled;
  const currentLambda = Number(draft.rag_mmr_lambda ?? 0.7);
  const currentWarm = Number(draft.rag_mmr_warm_start ?? 4);
  const isDefaultLambda = currentLambda === server.defaults.rag_mmr_lambda;
  const isDefaultWarm = currentWarm === server.defaults.rag_mmr_warm_start;

  return (
    <section
      data-testid="admin-platform-rag-panel"
      className="border hairline rounded-lg bg-[#FDFCF8] p-6 space-y-5"
    >
      {/* Header ─────────────────────────────────────────────── */}
      <header className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0 h-10 w-10 rounded-lg bg-[#4A5D4E]/10 flex items-center justify-center">
          <Brain size={22} weight="duotone" className="text-[#4A5D4E]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Retrieval Diversity (MMR)</h2>
            <span
              data-testid="admin-mmr-status-pill"
              className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded font-medium ${
                enabled
                  ? "bg-[#E6F2E6] text-[#2F6B3A]"
                  : "bg-[#F5EBD9] text-[#6B5A3E]"
              }`}
            >
              {enabled ? "ON · Platform-wide" : "OFF · Legacy top-K"}
            </span>
          </div>
          <div className="text-[11px] text-[#6B6A68] mt-1 tracking-[0.12em]">
            Foundational retrieval control — affects Public QA · YT Replies · AI Assistant · Battle · Probe
          </div>
        </div>
      </header>

      {/* Long-form explanation ───────────────────────────────── */}
      <div className="text-[13px] leading-relaxed text-[#3A3A38] space-y-3 border-l-2 border-[#4A5D4E]/40 pl-4">
        <p>
          <strong>What MMR is</strong> — Maximal Marginal Relevance is a
          textbook re-ranker used by essentially every production RAG
          system. After the retrieval engine computes the raw top-K
          chunks for a query (via semantic + lexical scoring), MMR walks
          down that list and, for each remaining slot, asks:
          <em> &quot;would this chunk add real new information, or is it a
          near-duplicate of what I&apos;ve already picked?&quot;</em>
        </p>
        <p>
          <strong>Why it exists</strong> — a fan&apos;s question is often
          multi-part (&quot;your pit station is sick, how do I get mine setup
          like that. Also. how do I get rid of side to side slop in my
          Servo? Are those torx screws?&quot;). Without MMR, the topic with
          the strongest keyword coverage (steering / servo) floods every
          slot in top-12 and crowds out chunks that answer the other
          parts (pit station, torx). The LLM then answers only the
          dominant topic and hallucinates on the rest. With MMR, the
          minority topics survive.
        </p>
        <p>
          <strong>How the algorithm works</strong> — for each remaining
          slot, MMR picks the chunk that maximizes{" "}
          <code className="text-[11px] bg-[#F0F1EE] px-1.5 py-0.5 rounded">
            λ · relevance − (1 − λ) · max_similarity_to_selected
          </code>. λ (lambda) is the diversity balance: 1.0 = pure
          top-K by relevance (current legacy behavior); 0.0 = pure
          diversity ignoring relevance (bad). Industry standard for RAG
          is 0.5–0.7. The similarity term uses embedding-vector cosine,
          not doc identity — so multiple verses about &quot;opposition in
          all things&quot; from different scriptures survive because they&apos;re
          not near-identical, only truly redundant paraphrases get
          filtered.
        </p>
        <p>
          <strong>Warm-start protection</strong> — the first N slots
          (default 4) are ALWAYS taken as-is by pure relevance, before
          MMR is applied. This preserves deep-topic coverage for
          single-topic queries (theology deep-dives, doctrine
          explanations) — the top 4 most-relevant chunks on the main
          topic are guaranteed. Only slots 5-12 are diversified.
        </p>
        <p>
          <strong>What this means for silos</strong> — theology / doctrine
          silos (Cosmic, Cosmic Equivalence) keep their deep coverage
          intact via warm-start; product / how-to silos (East Tactics,
          Randy&apos;s RC content, tutorial channels) get much better
          multi-topic answers because minority-topic chunks stop
          getting crowded out. Zero LLM cost delta — same TOP_K to the
          model, different mix. ~5-8 ms retrieval latency delta —
          negligible.
        </p>
      </div>

      {/* Impact strip — three quick tiles ────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
        <div className="border hairline rounded-md px-3 py-2 flex items-start gap-2">
          <ArrowsOutSimple size={14} weight="duotone" className="mt-0.5 shrink-0 text-[#4A5D4E]" />
          <div>
            <div className="font-medium text-[#1C1B1A]">Multi-topic fan questions</div>
            <div className="text-[#6B6A68]">Minority-topic chunks stop being crowded out. ~60-70% of real YouTube traffic.</div>
          </div>
        </div>
        <div className="border hairline rounded-md px-3 py-2 flex items-start gap-2">
          <ShieldStar size={14} weight="duotone" className="mt-0.5 shrink-0 text-[#4A5D4E]" />
          <div>
            <div className="font-medium text-[#1C1B1A]">Deep-topic queries protected</div>
            <div className="text-[#6B6A68]">Warm-start reserves the top 4 slots for pure relevance — theology stays deep.</div>
          </div>
        </div>
        <div className="border hairline rounded-md px-3 py-2 flex items-start gap-2">
          <ChartLineUp size={14} weight="duotone" className="mt-0.5 shrink-0 text-[#4A5D4E]" />
          <div>
            <div className="font-medium text-[#1C1B1A]">Zero LLM cost impact</div>
            <div className="text-[#6B6A68]">Same TOP_K reaches the model — different mix, same tokens.</div>
          </div>
        </div>
      </div>

      {/* Master toggle ───────────────────────────────────────── */}
      <div className="border hairline rounded-md bg-white p-4 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[#1C1B1A] font-medium">Enable MMR platform-wide</div>
          <div className="text-[11px] text-[#6B6A68] mt-0.5">
            When OFF, retrieval falls back to reserved-lexical top-K (previous behavior — still an
            improvement over raw top-K but without the multi-topic diversity fix).
          </div>
        </div>
        <label className="inline-flex items-center cursor-pointer shrink-0">
          <input
            type="checkbox"
            checked={enabled}
            data-testid="admin-mmr-enabled-toggle"
            disabled={busy}
            onChange={(e) => setDraft({ ...draft, rag_mmr_enabled: e.target.checked })}
            className="sr-only peer"
          />
          <div className="relative w-11 h-6 bg-[#E4E1DB] peer-checked:bg-[#4A5D4E] rounded-full transition-colors peer-focus:ring-2 peer-focus:ring-[#4A5D4E]/30">
            <div className={`absolute top-0.5 left-0.5 h-5 w-5 bg-white rounded-full shadow transition-transform ${enabled ? "translate-x-5" : ""}`} />
          </div>
        </label>
      </div>

      {/* Advanced (collapsed by default) ──────────────────────── */}
      <button
        onClick={() => setAdvancedOpen((s) => !s)}
        data-testid="admin-mmr-advanced-toggle"
        className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[#6B6A68] hover:text-[#4A5D4E] transition-colors"
      >
        {advancedOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
        <span>Advanced parameters</span>
      </button>

      {advancedOpen && (
        <div className="border hairline rounded-md bg-white p-4 space-y-4">
          {/* Lambda ─────────────────────────────────── */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm text-[#1C1B1A] font-medium">λ (Lambda) — relevance vs. diversity balance</label>
              <span className="text-[10px] font-mono text-[#6B6A68]">
                current: {currentLambda.toFixed(2)}
                {!isDefaultLambda && <span className="ml-1 text-[#D4A24E]">· modified</span>}
              </span>
            </div>
            <input
              type="range"
              min="0" max="1" step="0.05"
              value={currentLambda}
              data-testid="admin-mmr-lambda-slider"
              disabled={busy || !enabled}
              onChange={(e) => setDraft({ ...draft, rag_mmr_lambda: Number(e.target.value) })}
              className="w-full"
            />
            <div className="flex justify-between text-[10px] text-[#6B6A68] mt-1 font-mono">
              <span>0.0 · pure diversity</span>
              <span className="text-[#4A5D4E]">0.7 · industry default</span>
              <span>1.0 · pure top-K</span>
            </div>
            <div className="text-[11px] text-[#6B6A68] mt-1.5 flex items-start gap-1.5">
              <Info size={11} className="mt-0.5 shrink-0" />
              <span>
                Higher = more weight on raw relevance. Lower = more diversity in the tail. 0.7 is
                the value used by most production RAG systems (LangChain, LlamaIndex, Weaviate
                defaults). Don&apos;t touch this unless you have a specific silo where multi-topic
                answers feel too spread out — bump it toward 0.85 in that case.
              </span>
            </div>
          </div>

          {/* Warm-start ─────────────────────────────── */}
          <div className="pt-4 border-t hairline">
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm text-[#1C1B1A] font-medium">Warm-start slots — pure-relevance protection</label>
              <span className="text-[10px] font-mono text-[#6B6A68]">
                default 4{!isDefaultWarm && <span className="ml-1 text-[#D4A24E]">· modified</span>}
              </span>
            </div>
            <FreeNumberInput
              value={currentWarm}
              onChange={(v) => setDraft({ ...draft, rag_mmr_warm_start: v })}
              data-testid="admin-mmr-warm-start-input"
              disabled={busy || !enabled}
              min={0} max={12}
              className="w-24"
            />
            <div className="text-[11px] text-[#6B6A68] mt-1.5 flex items-start gap-1.5">
              <Info size={11} className="mt-0.5 shrink-0" />
              <span>
                First N slots are ALWAYS taken as-is by combined score (semantic + lexical) before
                MMR kicks in. Higher = more raw-relevance chunks up front (better for deep-topic
                queries). Lower = MMR starts earlier (better for multi-topic fan questions). 0 = MMR
                from slot 1 (pure diversity mode). 12 = disable MMR entirely (all slots by
                relevance — equivalent to turning MMR off).
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Actions ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 pt-2 border-t hairline">
        <button
          onClick={save}
          disabled={busy || dirtyKeys.length === 0}
          data-testid="admin-mmr-save-btn"
          className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4D3E] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <FloppyDisk size={14} weight="fill" />
          {busy ? "Saving…" : dirtyKeys.length > 0 ? `Save ${dirtyKeys.length} change${dirtyKeys.length === 1 ? "" : "s"}` : "Saved"}
        </button>
        <button
          onClick={resetToDefaults}
          disabled={busy}
          data-testid="admin-mmr-reset-btn"
          className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border hairline hover:bg-[#F0F1EE] text-[#6B6A68] disabled:opacity-40 transition-colors"
        >
          <ArrowCounterClockwise size={13} />
          Reset to defaults
        </button>
        <div className="ml-auto text-[10px] text-[#6B6A68] tracking-[0.12em]">
          Changes hot-reload · no pod restart
        </div>
      </div>
    </section>
  );
}
